import React from 'react';

import { Card } from '@lightspeed/flame/Card/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { createReactRenderer } from 'helpers/react-renderer';
import { REMOTE_DEPOSIT_ADDED_EVENT } from 'payments/PaymentDepositModal/RemoteDepositAddedEvent';
import { fetchCustomerId, fetchPrimaryCustomerEmail } from 'root/apps/CreditAccountStatement/utils';
import Format from 'root/apps/helpers/format';
import { useCheckRemoteDepositPaymentLink } from 'root/apps/payments/PaymentDepositModal/hooks/useCheckRemoteDepositPaymentLink';
import { useRemoteDepositPaymentLinkActions } from 'root/apps/payments/PaymentDepositModal/hooks/useRemoteDepositPaymentLinkActions';
import TooltipHelp from 'root/apps/Tooltip/components/TooltipHelp';

import { RemoteDepositPaymentLinkRequestedAPIPayload } from './types';

interface PaymentLinkDepositTileProps {
  saleID: number;
}

const PaymentLinkDepositTile: React.FC<PaymentLinkDepositTileProps> = ({ saleID }) => {
  const [customerPrimaryEmail, setCustomerPrimaryEmail] = React.useState('');
  const [remoteDepositPaymentRequest, setRemoteDepositPaymentRequest] =
    React.useState<RemoteDepositPaymentLinkRequestedAPIPayload | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [refreshIterator, setRefreshIterator] = React.useState(0);

  const {
    isLoading,
    editCurrentRemoteDepositPaymentRequest,
    removeCurrentRemoteDepositPaymentRequest,
  } = useRemoteDepositPaymentLinkActions(saleID) || {};

  const { currentSaleRemoteDepositPaymentLinkRequest } = useCheckRemoteDepositPaymentLink(
    saleID,
    refreshIterator
  );

  React.useEffect(() => {
    const handleRemoteDepositAddedEvent = () => {
      setRefreshIterator((prev) => prev + 1);
    };
    window.removeEventListener(REMOTE_DEPOSIT_ADDED_EVENT, handleRemoteDepositAddedEvent);
    window.addEventListener(REMOTE_DEPOSIT_ADDED_EVENT, handleRemoteDepositAddedEvent);
    return () => {
      window.removeEventListener(REMOTE_DEPOSIT_ADDED_EVENT, handleRemoteDepositAddedEvent);
    };
  }, [setRefreshIterator]);

  React.useEffect(() => {
    if (currentSaleRemoteDepositPaymentLinkRequest) {
      const {
        data: { amount, url, sendReceipt },
      } = currentSaleRemoteDepositPaymentLinkRequest.data;
      setRemoteDepositPaymentRequest({ amount, url, sendReceipt });
    }
  }, [currentSaleRemoteDepositPaymentLinkRequest, refreshIterator]);

  React.useEffect(() => {
    const fetchCustomerEmailForTooltip = async () => {
      if (remoteDepositPaymentRequest) {
        const customerID = fetchCustomerId();

        if (!customerID) {
          setCustomerPrimaryEmail('');
        } else {
          const customerPrimaryEmail = await fetchPrimaryCustomerEmail(customerID);
          setCustomerPrimaryEmail(customerPrimaryEmail);
        }
      }
    };

    setIsEditing(false);
    fetchCustomerEmailForTooltip();
  }, [remoteDepositPaymentRequest]);

  const generateTooltipContent = () => {
    const baseToolTipContent = (
      <span>A payment link will be generated once you finish the sale.</span>
    );

    if (remoteDepositPaymentRequest && remoteDepositPaymentRequest.sendReceipt) {
      return (
        <span>
          A payment link will be sent to: <var>{customerPrimaryEmail}</var> once you finish the
          sale.
        </span>
      );
    }

    return baseToolTipContent;
  };

  return (
    <>
      {remoteDepositPaymentRequest ? (
        <>
          <Card mb={2} id={`remote-payment-deposit-tile${isEditing ? '-editing' : '-active'}`}>
            <Flex p={2} flexDirection="column">
              <Box mb={2}>
                <Text fontWeight="bold" fontSize="14px">
                  Pending deposit
                </Text>
              </Box>
              <Flex justifyContent="space-between">
                <Text color="#8B939B">Via payment link</Text>
                <Text data-test="remote-deposit-payment-request-amount">
                  <var className="notranslate">
                    {Format.toMoney(remoteDepositPaymentRequest.amount)}
                  </var>
                </Text>
              </Flex>
            </Flex>
            <Flex
              backgroundColor="#F2F5F8"
              justifyContent="space-between"
              style={{ borderTop: '1px solid #D7DCE1' }}
              p={2}
            >
              <Box>
                <TooltipHelp
                  content={generateTooltipContent()}
                  width="210px"
                  style={{ wordBreak: 'break-word' }}
                  placement="top-start"
                >
                  <Box>
                    <IconInfo size="16px" data-test="tooltip-icon" />
                  </Box>
                </TooltipHelp>
              </Box>
              <Flex>
                {editCurrentRemoteDepositPaymentRequest && (
                  <TextLink
                    fontWeight="bold"
                    mr={2}
                    style={{ pointerEvents: isLoading ? 'none' : 'inherit' }}
                    onClick={() => {
                      setIsEditing(true);
                      editCurrentRemoteDepositPaymentRequest();
                    }}
                    data-test="edit-remote-deposit-payment-request"
                  >
                    Edit
                  </TextLink>
                )}
                {removeCurrentRemoteDepositPaymentRequest && (
                  <TextLink
                    fontWeight="bold"
                    style={{ pointerEvents: isLoading ? 'none' : 'inherit' }}
                    onClick={() => {
                      removeCurrentRemoteDepositPaymentRequest();
                      setRemoteDepositPaymentRequest(null);
                    }}
                    data-test="remove-remote-deposit-payment-request"
                  >
                    Remove
                  </TextLink>
                )}
              </Flex>
            </Flex>
          </Card>
        </>
      ) : null}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: PaymentLinkDepositTile,
  selector: '[data-react-root="PaymentLinkDepositTile"]',
});

history.onChangeState(
  { pathname: '/register.php', form_name: undefined, id: undefined, view: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default PaymentLinkDepositTile;
